import { useState, useEffect } from 'react';

import { useAuthToken } from '../../../../hooks/useAuthToken';
import { useResponsive } from '../../../../hooks/useResponsive';
import { secretsApi, SupabaseSecret } from '../../../../lib/api/supabase';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { cn } from '../../../../lib/utils';
import { Search, Eye, EyeOff, Trash2, Plus, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

interface SecretsPanelProps {
  projectId: string;
}

interface NewSecret {
  name: string;
  value: string;
  showValue: boolean;
}

export function SecretsPanel({ projectId }: SecretsPanelProps) {
  const { token } = useAuthToken();
  const { isMobile } = useResponsive();
  const [secrets, setSecrets] = useState<SupabaseSecret[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [newSecrets, setNewSecrets] = useState<NewSecret[]>([
    { name: '', value: '', showValue: false },
  ]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (token) {
      loadSecrets();
    }
  }, [projectId, token]);

  const loadSecrets = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await secretsApi.listSecrets(token, projectId);
      setSecrets(data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load secrets: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const addSecretRow = () => {
    setNewSecrets([...newSecrets, { name: '', value: '', showValue: false }]);
  };

  const removeSecretRow = (index: number) => {
    setNewSecrets(newSecrets.filter((_, i) => i !== index));
  };

  const updateNewSecret = (index: number, field: 'name' | 'value', value: string) => {
    const updated = [...newSecrets];
    updated[index][field] = value;
    setNewSecrets(updated);
  };

  const toggleShowValue = (index: number) => {
    const updated = [...newSecrets];
    updated[index].showValue = !updated[index].showValue;
    setNewSecrets(updated);
  };

  const validateSecrets = (): boolean => {
    // Check for empty fields
    const hasEmptyFields = newSecrets.some(s => !s.name.trim() || !s.value.trim());
    if (hasEmptyFields) {
      toast.error('All secret fields must be filled');
      return false;
    }

    // Validate name format (uppercase letters, numbers, underscores only)
    const invalidNames = newSecrets.filter(s => !/^[A-Z0-9_]+$/.test(s.name));
    if (invalidNames.length > 0) {
      toast.error(
        'Secret names must contain only uppercase letters, numbers, and underscores'
      );
      return false;
    }

    // Check for duplicates in new secrets
    const names = newSecrets.map(s => s.name);
    const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
    if (duplicates.length > 0) {
      toast.error(`Duplicate secret names: ${[...new Set(duplicates)].join(', ')}`);
      return false;
    }

    // Check if any names already exist
    const existingNames = secrets.map(s => s.name);
    const conflicts = names.filter(name => existingNames.includes(name));
    if (conflicts.length > 0) {
      toast.error(
        `Secret names already exist: ${conflicts.join(', ')}. Delete them first or use different names.`
      );
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!token) return;
    if (!validateSecrets()) return;

    setSaving(true);
    try {
      const secretsToCreate = newSecrets.map(s => ({ name: s.name, value: s.value }));
      await secretsApi.createSecrets(token, projectId, secretsToCreate);
      toast.success(`${secretsToCreate.length} secret(s) created successfully`);
      setNewSecrets([{ name: '', value: '', showValue: false }]);
      await loadSecrets();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to create secrets: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (secretName: string) => {
    setSecretToDelete(secretName);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!token || !secretToDelete) return;

    setDeleting(true);
    try {
      await secretsApi.deleteSecrets(token, projectId, [secretToDelete]);
      toast.success(`Secret "${secretToDelete}" deleted`);
      setDeleteModalOpen(false);
      setSecretToDelete(null);
      await loadSecrets();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      toast.error(`Failed to delete: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  const filteredSecrets = secrets.filter(secret =>
    secret.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
          <span>Loading secrets...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", isMobile && "space-y-3")}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={cn(isMobile ? "text-base" : "text-lg", "font-semibold")}>Secrets</h3>
          {!isMobile && (
            <p className="text-sm text-muted-foreground mt-0.5">
              Environment variables for Edge Functions
            </p>
          )}
        </div>
        <Button onClick={loadSecrets} variant="ghost" size="sm">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Add New Secrets */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Add new</h4>
          <Button
            onClick={handleSave}
            disabled={saving || newSecrets.every(s => !s.name.trim() && !s.value.trim())}
            size="sm"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        <div className={cn("rounded-lg border border-border/60 p-4", isMobile ? "space-y-4" : "space-y-3")}>
          {newSecrets.map((secret, index) => (
            <div key={index} className={cn(
              isMobile ? "flex flex-col gap-2" : "flex gap-3 items-center"
            )}>
              <Input
                type="text"
                placeholder="KEY_NAME"
                value={secret.name}
                onChange={(e) => updateNewSecret(index, 'name', e.target.value.toUpperCase())}
                className={cn("h-9 font-mono text-sm bg-muted/30", !isMobile && "flex-1")}
              />
              <div className={cn("relative", !isMobile && "flex-1")}>
                <Input
                  type={secret.showValue ? 'text' : 'password'}
                  placeholder="Value"
                  value={secret.value}
                  onChange={(e) => updateNewSecret(index, 'value', e.target.value)}
                  className="h-9 pr-9 text-sm bg-muted/30"
                />
                <button
                  type="button"
                  onClick={() => toggleShowValue(index)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded text-muted-foreground"
                >
                  {secret.showValue ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeSecretRow(index)}
                disabled={newSecrets.length === 1}
                className={cn(
                  "text-muted-foreground hover:text-destructive disabled:opacity-30",
                  isMobile ? "h-8 self-end" : "h-9 w-9 p-0"
                )}
              >
                <Trash2 className="h-4 w-4" />
                {isMobile && <span className="ml-1.5 text-xs">Remove</span>}
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addSecretRow}
            className="h-8 text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add row
          </Button>
        </div>
      </div>

      {/* Existing Secrets */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">
            Existing secrets
            {secrets.length > 0 && <span className="text-muted-foreground font-normal ml-1">({secrets.length})</span>}
          </h4>
          {secrets.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 h-8 pl-9 text-sm"
              />
            </div>
          )}
        </div>

        {filteredSecrets.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="p-3 rounded-full bg-muted/50 mb-3">
                <Search className="w-5 h-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'No secrets match your search' : 'No secrets yet'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSecrets.map((secret) => (
              <div
                key={secret.name}
                className="rounded-lg border border-border/60 p-4 flex items-center justify-between hover:border-border transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-muted">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-medium truncate">{secret.name}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="font-mono">sha256</span>
                      <span>·</span>
                      <span>Updated {new Date(secret.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => confirmDelete(secret.name)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete secret</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              This will permanently remove the secret. Your Edge Functions will no longer have access to it.
            </p>
            {secretToDelete && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border/40">
                <Eye className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="font-mono text-sm truncate">{secretToDelete}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setSecretToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
